<template>
  <transition-group name="board" tag="section" id="board" :style="boardStyle">
    <div
      v-for="(row, rowIndex) in boardData.slice().reverse()"
      :key="reverseIndex(rowIndex)"
      @click="toggleSelectedRow(reverseIndex(rowIndex))"
      :class="{
        selected: selectedRows[rowIndex],
        locked: true,
      }"
      class="board-row"
    >
      <div
        class="board-cell"
        v-for="(value, columnIndex) in row"
        :key="columnIndex"
      >
        <div
          class="shape"
          :class="highlightShape(value, rowIndex, columnIndex)"
        ></div>
        <div class="value">
          {{ value }}
        </div>
      </div>
    </div>
  </transition-group>
  <sequence-help
    :intersectionSet="intersectionSet"
    :totalRows="totalRows"
    :selectedRowCount="selectedRowCount"
    :tooManyClicks="tooManyClicks"
  ></sequence-help>
</template>

<script>
import SequenceHelp from "./SequenceHelp.vue";
export default {
  components: { SequenceHelp },
  name: "KnotBoardDemo",
  data() {
    return {
      sequenceLength: 4,
      selectedRows: [],
      intersectionSet: new Set(),
      intersectionSequencesIndices: new Set(),
      helpText: "",
      status: null,
      solved: false,
      tooManyClicks: false,
    };
  },
  inject: ["trackEvent"],
  props: {
    level: {
      type: Number,
      required: true,
    },
  },
  watch: {
    boardData: {
      handler: function (newValue) {
        this.selectedRows = Array(newValue.length).fill(false) || [];
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {},
  computed: {
    boardStyle() {
      return { height: this.boardHeight };
    },
    boardHeight() {
      return `calc(var(--board-cell-dimension-demo) * ${
        this.boardData.length + 1
      } )`;
    },
    boardData() {
      let board = [
        [1, 44, 45, 89],
        [3, 21, 24, 45],
        [3, 42, 45, 87],
        [21, 24, 45, 69],
      ];
      if (this.level >= 11) {
        board.unshift([1, 43, 44, 87]);
      }
      if (this.level >= 12) {
        board.unshift([1, 42, 43, 85]);
      }
      return board;
    },
    selectedRowCount() {
      return this.selectedRows.filter((item) => item === true).length;
    },
    totalRows() {
      return this.boardData.length;
    },
  },
  methods: {
    getIntersection(arr1, arr2) {
      return arr1.filter((value) => arr2.includes(value));
    },
    setIntersectionSetInfo() {
      const intersectionSet = new Set();
      this.intersectionSequencesIndices = new Set();
      for (let i = 0; i < this.boardData.length; i++) {
        const reversedI = this.reverseIndex(i);
        if (!this.selectedRows[reversedI]) {
          continue;
        }
        for (let j = i + 1; j < this.boardData.length; j++) {
          const reversedJ = this.reverseIndex(j);
          if (!this.selectedRows[reversedJ]) {
            continue;
          }
          const intersection = this.getIntersection(
            this.boardData[i],
            this.boardData[j]
          );
          if (intersection.length !== 0) {
            this.intersectionSequencesIndices.add(i);
            this.intersectionSequencesIndices.add(j);
          }
          intersection.forEach((item) => intersectionSet.add(item));
        }
      }

      this.intersectionSet = intersectionSet;
    },
    reverseIndex(rowIndex) {
      return this.totalRows - rowIndex - 1;
    },
    highlightShape(value, rowIndex) {
      let classes = new Object();
      if (this.selectedRows[rowIndex] && this.intersectionSet.has(value)) {
        classes["knot-background"] = true;
      }
      return classes;
    },
    async toggleSelectedRow(rowIndex) {
      const reversedIndex = this.reverseIndex(rowIndex);
      if (!this.selectedRows[reversedIndex] && this.selectedRowCount === 3) {
        this.tooManyClicks = true;
        return;
      } else {
        this.tooManyClicks = false;
      }
      this.selectedRows[reversedIndex] = await !this.selectedRows[
        reversedIndex
      ];
      this.setIntersectionSetInfo();
    },
  },
};
</script>

<style scoped>
* {
  font-family: "Fira Sans", system-ui;
}
#board {
  flex-direction: column;
  row-gap: var(--board-spacing);
  align-items: center;
  justify-content: center;
}

.board-row {
  display: flex;
  column-gap: var(--board-spacing);
  justify-content: center;
  align-items: stretch;
}

.board-cell {
  display: flex;
  aspect-ratio: 1/ 1;
  transition: all 0.125s cubic-bezier(0.94, 0.77, 0.57, 1.25);
  height: calc(var(--board-cell-dimension-demo));
  font-size: calc(var(--board-cell-dimension-demo) * 0.5);
  justify-content: center;
  align-items: center;
  user-select: none;
  font-weight: bold;
  position: relative;
  touch-action: manipulation;
}

.shape {
  width: 100%;
  height: 100%;
  transform-origin: center; /* Ensures rotation happens around the center */
  position: absolute;
  background-color: var(--board-cell-color);
  border-radius: 5px;
  transition: all 0.3s ease; /* Smooth transition */
  border: 0.45vh solid rgba(0, 0, 0, 0);
}

.value {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  width: 100%;
  height: 100%;
  font-weight: bold;
  transition: color 0.6s ease;
}

.board-cell.locked {
  cursor: pointer;
}

.selected .board-cell {
  height: calc(var(--board-cell-dimension-demo) * 1.125);
  font-size: calc(var(--board-cell-dimension-demo) * 0.5 * 1.125);
}

.selected .shape {
  background-color: var(--selected-color);
}

.knot-background {
  border-radius: 50%;
  transform: rotate(720deg); /* Rotate to diamond */
}
.knot-background,
.red-herring-background {
  transition: all 1.5s ease, border 0.3s ease;
}

</style>